/**
 * O bloco <script type="application/json" id="portal-relatorio"> do relatório HTML.
 *
 * É o que a aba Dados lê e troca: medidas, colunas, títulos, rótulos, formatos, ordem,
 * quantidade, filtros fixos e se o bloco aparece. O código do relatório só usa os
 * apelidos, então trocar o valor aqui não quebra nada lá.
 */

export interface FiltroFixo { coluna: string; operador?: string; valor: unknown }
/** Filtro da barra que o SDK desenha: múltipla escolha com busca, aplicado em todos os blocos. */
export interface FiltroTela { id: string; coluna: string; rotulo?: string }
/**
 * O seletor de loja: no celular, o portal mostra as lojas perto da pessoa e abre o relatório já filtrado na que ela escolher.
 * As colunas e a medida são do dicionário do relatório.
 */
export interface SeletorLoja {
  /** O id do filtro da tela que recebe a loja escolhida. A coluna dele é a do código da loja. */
  filtro: string
  colunas: {
    codigo: string; nome: string; lat: string; lng: string
    endereco?: string; cidade?: string; uf?: string; bandeira?: string
  }
  /** Só entram na lista as lojas em que esta medida tem valor, dentro do que a pessoa pode ver. */
  medida?: string
  raioInicialM?: number
  /** Sem o botão "Ver todas as lojas". */
  obrigatorio?: boolean
}
export interface Formato { tipo?: string; casas?: number }
export interface ConsultaBloco {
  colunas?: Record<string, string>
  medidas?: Record<string, string>
  filtros?: FiltroFixo[]
  ordenarPor?: string
  direcao?: string
  limite?: number
}
export interface BlocoRelatorio {
  titulo?: string
  forma?: string
  visivel?: boolean
  /** Ids de filtros_tela que o bloco não segue, ou true para nenhum. */
  ignorarFiltros?: string[] | boolean
  consulta?: ConsultaBloco
  rotulos?: Record<string, string>
  formatos?: Record<string, Formato>
}
export interface ConfigRelatorio {
  textos?: Record<string, string>
  filtros_tela?: FiltroTela[]
  seletor_loja?: SeletorLoja
  blocos?: Record<string, BlocoRelatorio>
  [outra: string]: unknown
}

/**
 * Acha o script portal-relatorio de verdade, como o HtmlSeguro.BlocoConfig no servidor:
 * percorre as tags script em ordem, e o texto da tag citado dentro de outro script (o
 * comentário do SDK colado no head) é conteúdo daquele script, não conta.
 */
function acharBloco(html: string) {
  const tag = /(<script\b([^>]*)>)([\s\S]*?)(<\/script\s*>)/gi
  let m: RegExpExecArray | null
  while ((m = tag.exec(html)) !== null) {
    if (/\bid\s*=\s*["']portal-relatorio["']/i.test(m[2])) return { index: m.index, inteiro: m[0], abre: m[1], json: m[3], fecha: m[4] }
  }
  return null
}

export type Leitura =
  | { tipo: 'ok'; config: ConfigRelatorio }
  | { tipo: 'sem-bloco' }
  | { tipo: 'invalido'; erro: string }

export function lerConfig(html: string): Leitura {
  const m = acharBloco(html)
  if (!m) return { tipo: 'sem-bloco' }
  try {
    const c = JSON.parse(m.json)
    if (!c || typeof c !== 'object' || Array.isArray(c)) return { tipo: 'invalido', erro: 'O bloco precisa ser um objeto JSON.' }
    return { tipo: 'ok', config: c }
  } catch (e: any) {
    return { tipo: 'invalido', erro: e?.message ?? 'JSON inválido.' }
  }
}

/**
 * Troca só o conteúdo do bloco; o resto do HTML fica byte a byte igual. O "<" vira
 * < para nenhum texto fechar a tag script por engano.
 */
export function escreverConfig(html: string, config: ConfigRelatorio): string {
  const m = acharBloco(html)
  if (!m) throw new Error('O HTML não tem o bloco portal-relatorio.')
  const recuo = /\n([ \t]*)[^\n]*$/.exec(html.slice(0, m.index))?.[1] ?? ''
  const json = JSON.stringify(config, null, 2).replace(/</g, '\\u003c')
    .split('\n').map(l => recuo + '  ' + l).join('\n')
  return html.slice(0, m.index) + m.abre + '\n' + json + '\n' + recuo + m.fecha + html.slice(m.index + m.inteiro.length)
}

export const clonar = <T,>(x: T): T => JSON.parse(JSON.stringify(x))

/** Grava um valor num caminho ("blocos.x.consulta.limite"); undefined apaga a chave. */
export function definir<T extends object>(config: T, caminho: string[], valor: unknown): T {
  const novo: any = clonar(config)
  let atual = novo
  for (const parte of caminho.slice(0, -1)) {
    if (atual[parte] == null || typeof atual[parte] !== 'object') atual[parte] = {}
    atual = atual[parte]
  }
  const ultima = caminho[caminho.length - 1]
  if (valor === undefined) delete atual[ultima]
  else atual[ultima] = valor
  return novo
}

/** Lê um valor num caminho. */
export function ler(config: unknown, caminho: string[]): unknown {
  let atual: any = config
  for (const parte of caminho) {
    if (atual == null || typeof atual !== 'object') return undefined
    atual = atual[parte]
  }
  return atual
}

export interface Mudanca { caminho: string[]; rotulo: string; de: unknown; para: unknown }

// Folhas do objeto. Listas (os filtros) contam como um valor só.
function folhas(x: unknown, base: string[] = [], saida = new Map<string, unknown>()) {
  if (x && typeof x === 'object' && !Array.isArray(x)) {
    for (const k of Object.keys(x as object)) folhas((x as any)[k], [...base, k], saida)
  } else if (base.length) {
    saida.set(JSON.stringify(base), x)
  }
  return saida
}

function rotuloDoCaminho(c: string[]): string {
  if (c[0] === 'textos') return `Texto "${c[1]}"`
  if (c[0] === 'filtros_tela') return 'Filtros da tela'
  if (c[0] === 'seletor_loja') {
    const campo: Record<string, string> = {
      filtro: 'filtro da tela', medida: 'medida de referência', raioInicialM: 'raio inicial', obrigatorio: 'obrigatório',
      codigo: 'coluna do código', nome: 'coluna do nome', lat: 'coluna da latitude', lng: 'coluna da longitude',
      endereco: 'coluna do endereço', cidade: 'coluna da cidade', uf: 'coluna da UF', bandeira: 'coluna da bandeira'
    }
    return `Seletor de loja · ${campo[c[c.length - 1]] ?? c.slice(1).join('.')}`
  }
  if (c[0] !== 'blocos' || c.length < 3) return c.join('.')
  const b = c[1]
  const [parte, a, b3] = c.slice(2)
  if (parte === 'titulo') return `${b} · título`
  if (parte === 'visivel') return `${b} · aparece no relatório`
  if (parte === 'ignorarFiltros') return `${b} · filtros da tela que não segue`
  if (parte === 'rotulos') return `${b} · rótulo de "${a}"`
  if (parte === 'formatos') return `${b} · ${b3 === 'casas' ? 'casas decimais' : 'formato'} de "${a}"`
  if (parte === 'consulta') {
    if (a === 'medidas') return `${b} · medida de "${b3}"`
    if (a === 'colunas') return `${b} · coluna de "${b3}"`
    if (a === 'filtros') return `${b} · filtros fixos`
    if (a === 'ordenarPor') return `${b} · ordenar por`
    if (a === 'direcao') return `${b} · direção da ordem`
    if (a === 'limite') return `${b} · quantidade de linhas`
  }
  return c.join('.')
}

/** O que mudou de uma configuração para a outra, folha a folha. */
export function mudancas(original: ConfigRelatorio, atual: ConfigRelatorio): Mudanca[] {
  const a = folhas(original), b = folhas(atual)
  const chaves = Array.from(new Set([...Array.from(a.keys()), ...Array.from(b.keys())]))
  return chaves
    .filter(k => JSON.stringify(a.get(k)) !== JSON.stringify(b.get(k)))
    .map(k => {
      const caminho = JSON.parse(k) as string[]
      return { caminho, rotulo: rotuloDoCaminho(caminho), de: a.get(k), para: b.get(k) }
    })
}

/** Um valor de mudança para ler na lista de pendências. */
export function mostrarValor(v: unknown): string {
  if (v === undefined || v === null || v === '') return '(vazio)'
  if (v === true) return 'sim'
  if (v === false) return 'não'
  if (v === 'DESC') return 'maior primeiro'
  if (v === 'ASC') return 'menor primeiro'
  if (Array.isArray(v)) {
    if (v.length === 0) return 'nenhum'
    return v.map(f => (typeof f !== 'object' || f === null ? String(f)
      : 'id' in f ? String((f as FiltroTela).rotulo || (f as FiltroTela).id) : descreverFiltro(f as FiltroFixo))).join('; ')
  }
  return String(v)
}

export function descreverFiltro(f: FiltroFixo): string {
  const valor = Array.isArray(f.valor) ? f.valor.join(', ') : String(f.valor)
  return `${f.coluna} ${f.operador || '='} ${valor}`
}

const nomeDaColuna = (n: string) => (n.includes('[') ? n.slice(n.indexOf('[') + 1, n.lastIndexOf(']')) : n)

/** O seletor que se liga pela primeira vez: o código é a coluna do filtro, e latitude e longitude vêm do que o dicionário tiver com esse nome. */
export function novoSeletorLoja(filtro: FiltroTela, colunas: { nome: string }[]): SeletorLoja {
  const achar = (re: RegExp) => colunas.find(c => re.test(nomeDaColuna(c.nome)))?.nome
  return {
    filtro: filtro.id,
    colunas: {
      codigo: filtro.coluna,
      nome: filtro.coluna,
      lat: achar(/^lat(itude)?$/i) ?? '',
      lng: achar(/^(lng|lon|long|longitude)$/i) ?? ''
    },
    raioInicialM: 2000
  }
}

/** Troca o filtro do seletor: o código da loja passa a ser a coluna do filtro novo, e o nome acompanha quando era o próprio código. */
export function trocarFiltroDoSeletor(seletor: SeletorLoja, filtro: FiltroTela): SeletorLoja {
  const nomeEraCodigo = seletor.colunas.nome === seletor.colunas.codigo
  return {
    ...seletor,
    filtro: filtro.id,
    colunas: { ...seletor.colunas, codigo: filtro.coluna, nome: nomeEraCodigo ? filtro.coluna : seletor.colunas.nome }
  }
}

/** O que falta para o servidor aceitar o seletor, em frases para a tela. */
export function faltaNoSeletor(seletor: SeletorLoja): string[] {
  const falta: string[] = []
  if (!seletor.colunas.nome) falta.push('a coluna do nome da loja')
  if (!seletor.colunas.lat) falta.push('a coluna da latitude')
  if (!seletor.colunas.lng) falta.push('a coluna da longitude')
  return falta
}

/**
 * Onde o relatório usa cada item que o modelo não tem mais: blocos (colunas, medidas e filtros fixos), filtros da tela e seletor
 * de loja. A chave é o nome como está no dicionário; o valor, uma lista de lugares legível ("bloco sem_checkin").
 */
export function usosDosAusentes(config: ConfigRelatorio, ausentes: string[]): Record<string, string[]> {
  const usos: Record<string, string[]> = {}
  const marca = (nome: string | undefined, onde: string) => {
    if (!nome) return
    const achado = ausentes.find(a => a.trim().toLowerCase() === nome.trim().toLowerCase())
    if (!achado) return
    if (!usos[achado]) usos[achado] = []
    if (!usos[achado].includes(onde)) usos[achado].push(onde)
  }
  Object.entries(config.blocos ?? {}).forEach(([id, bloco]) => {
    const onde = `bloco ${id}`
    Object.values(bloco.consulta?.colunas ?? {}).forEach(n => marca(n, onde))
    Object.values(bloco.consulta?.medidas ?? {}).forEach(n => marca(n, onde))
    ;(bloco.consulta?.filtros ?? []).forEach(f => marca(f.coluna, onde))
  })
  ;(config.filtros_tela ?? []).forEach(f => marca(f.coluna, `filtro da tela "${f.rotulo || f.id}"`))
  const s = config.seletor_loja
  if (s) {
    Object.values(s.colunas ?? {}).forEach(n => marca(n, 'seletor de loja'))
    marca(s.medida, 'seletor de loja')
  }
  return usos
}

/** Medidas com cara de percentual pelo nome, como o SDK decide quando não há formato. */
export const parecePercentual = (nome: string) => /%|percent|taxa|share/i.test(nome)
